// The render-error boundary (APP-MONITORING M1.5), same implementation as
// apps/physio-app/src/components/ErrorBoundary.tsx — React error boundaries
// must be class components, still the one deliberate exception to this app's
// function-component convention.
//
// ONE BOUNDARY, AT THE ROOT (main.tsx), unlike physio-app's root+panel split:
// there is no "the call keeps running while one panel recovers" concept on
// this side — CallSurface/CallRoom are the whole of what's on screen once a
// patient joins, so a crash anywhere in that tree is the whole page's crash.
import React from 'react';

import { captureError, type TelemetryContext } from '../monitoring/telemetry';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Rendered in place of `children` after a catch. Receives `retry` and the one event id this catch produced. */
  fallback: (info: { error: Error; eventId: string; retry: () => void }) => React.ReactNode;
  /** Allow-listed context attached to the captured event (M1.4). */
  context?: TelemetryContext;
  onError?: (error: Error) => void;
}

interface State {
  error: Error | null;
  eventId: string | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  state: State = { error: null, eventId: null };

  static getDerivedStateFromError(error: unknown): Pick<State, 'error'> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown): void {
    const captured = error instanceof Error ? error : new Error(String(error));
    // Exactly one crash event per catch: captured here, once, in the
    // lifecycle method React guarantees runs exactly once per catch.
    const { eventId } = captureError(captured, this.props.context);
    this.setState({ eventId });
    this.props.onError?.(captured);
  }

  retry = (): void => {
    this.setState({ error: null, eventId: null });
  };

  render(): React.ReactNode {
    const { error, eventId } = this.state;
    if (error) return this.props.fallback({ error, eventId: eventId ?? '', retry: this.retry });
    return this.props.children;
  }
}
